import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { OrderCustomer } from '../components/Customers.tsx';
import { Icon } from '../components/Icon.tsx';
import { ProductTile } from '../components/ProductTile.tsx';
import { isMaster, VariantDialog } from '../components/VariantDialog.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { clock, money, qty as fmtQty } from '../lib/format.ts';
import { priceNow } from '../lib/menu.ts';
import { usePlaces } from '../lib/places.tsx';
import { linePrice } from '../lib/taxes.ts';
import { useSession } from '../lib/session.tsx';
import type { Catalog, Line, OrderView } from '../lib/types.ts';
import { useNow, useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { CancelDialog, DiscountDialog } from './Order.tsx';
import { PayDialog, useRegister } from './Pay.tsx';
import { PromoDialog } from '../components/PromoDialog.tsx';

/**
 * The shop till: scan the goods, take the money, next customer. A barcode scanner types the
 * code and Enter into the box at the top; letters search the products instead. A sale can be
 * held under a name while the customer fetches something, and taken up again later.
 */

interface Added {
  product: string;
  name: string;
  qty: number;
  label: { kind: 'weight' | 'price'; value: number; rule: string } | null;
}
interface HeldSale {
  id: string;
  number: number;
  name: string;
  total: number;
  items: number;
  opened_at: string;
  by_name: string | null;
}
interface ScaleRule {
  id: string;
  name: string;
  prefix: string;
  kind: 'weight' | 'price';
  item_digits: number;
  decimals: number;
  active: boolean;
}

/** A code typed or scanned: digits only (a few separators allowed by some scanners). */
const isCode = (s: string) => /^\d{3,}$/.test(s);

export function Till() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fail, toast } = useUi();
  const { can } = useSession();
  const now = useNow();
  const { here, multi } = usePlaces();
  const location = multi && here ? here.id : null;
  const [view, setView] = useState<OrderView | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [last, setLast] = useState<{ added: Added; at: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<null | 'pay' | 'hold' | 'held' | 'discount' | 'promo' | 'cancel' | 'scale' | 'display' | { qty: Line } | { vary: string }>(null);
  const [heldVersion, setHeldVersion] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const starting = useRef<Promise<string> | null>(null);
  useEffect(() => {
    if (!id) starting.current = null;
  }, [id]);
  const { data: held } = useAction<HeldSale[]>('restaurant.till_sales', { location, v: heldVersion });

  // The sale on screen, when there is one; a sale already paid or cancelled starts a new one.
  const reload = useCallback(async () => {
    if (!id) return setView(null);
    try {
      const v = await action<OrderView>('restaurant.order', { order: id });
      if (v.order.state !== 'open') navigate('/till', { replace: true });
      else setView(v);
    } catch (err) {
      fail(err);
      navigate('/till', { replace: true });
    }
  }, [id, fail, navigate]);
  useEffect(() => void reload(), [reload]);

  const saleLocation = view?.order.location ?? location;
  // The customer display at this register follows the sale on screen (the payment dialog shows the amount itself).
  const { registers, register, choose } = useRegister(saleLocation);
  const displayReg = register?.id;
  const shown = view?.order.id ?? null;
  useEffect(() => {
    if (!displayReg) return;
    void action('pos.display_show', { register: displayReg, order: shown }).catch(() => undefined);
  }, [displayReg, shown]);
  const openDisplay = (reg: string) => window.open(`/display/${reg}`, `erp-display-${reg}`, 'popup,width=1024,height=700');
  useEffect(() => {
    action<Catalog>('products.catalog', { location: saleLocation ?? null }).then(setCatalog, fail);
  }, [fail, saleLocation]);

  const focus = useCallback(() => requestAnimationFrame(() => input.current?.focus()), []);
  useEffect(() => {
    if (!dialog) focus();
  }, [dialog, focus, id]);

  const show = (v: OrderView) => {
    setView(v);
    setHeldVersion((n) => n + 1);
    if (v.order.id !== id) navigate(`/till/${v.order.id}`, { replace: true });
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setProblem(null);
    try {
      await fn();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      focus();
    }
  };

  const scan = (code: string) =>
    run(async () => {
      const r = await action<OrderView & { added: Added }>('restaurant.till_scan', { order: view?.order.id ?? null, code, location });
      setLast({ added: r.added, at: Date.now() });
      setText('');
      show(r);
    });

  const addProduct = (product: string) =>
    run(async () => {
      // Two items tapped quickly go on the same new sale, never on two.
      const order = view?.order.id ?? (await (starting.current ??= action<OrderView>('restaurant.till_start', { location }).then(
            (v) => v.order.id,
            (err) => {
              starting.current = null;
              throw err;
            },
          )));
      const r = await action<OrderView>('restaurant.add_items', { order, items: [{ product, qty: 1, modifiers: [] }] });
      setLast({ added: { product, name: productName(product), qty: 1, label: null }, at: Date.now() });
      setText('');
      show(r);
    });

  const setQty = (line: Line, qty: number) =>
    run(async () => {
      show(
        qty > 0
          ? await action<OrderView>('restaurant.update_line', { line: line.id, qty })
          : await action<OrderView>('restaurant.remove_line', { line: line.id }),
      );
    });

  const products = useMemo(() => {
    const list = (catalog?.products ?? []).filter((p) => p.kind !== 'combo' && p.on_menu !== false);
    const q = text.trim().toLowerCase();
    if (q && !isCode(q)) return list.filter((p) => !isMaster(p) && p.name.toLowerCase().includes(q)).slice(0, 24);
    const cat = category ?? catalog?.categories[0]?.id;
    return list.filter((p) => p.category === cat && !p.variant_of);
  }, [catalog, category, text]);
  const productName = (pid: string) => catalog?.products.find((p) => p.id === pid)?.name ?? '';

  const submit = () => {
    const q = text.trim();
    if (!q || busy) return;
    if (isCode(q)) void scan(q);
    else if (products.length === 1) void addProduct(products[0]!.id);
    else if (!products.length) setProblem(t('till.no_match', { text: q }));
  };

  const order = view?.order ?? null;
  const active = (view?.lines ?? []).filter((l) => l.state === 'active');
  const others = (held ?? []).filter((h) => h.id !== order?.id);
  const fresh = last && Date.now() - last.at < 4000 ? last.added : null;

  return (
    <Shell>
      <div className="till-layout">
        <header className="till-head">
          <div>
            <h1>{t('till.title')}</h1>
            <p className="muted">{order ? t('till.sale_no', { n: order.number, name: order.name === 'Sale' ? t('till.sale') : order.name }) : t('till.ready')}</p>
          </div>
          <div className="till-head-actions">
            <Button icon="pause" onClick={() => setDialog('held')} disabled={!others.length}>
              {t('till.held')}
              {others.length ? <span className="count">{others.length}</span> : null}
            </Button>
            <IconButton icon="layout" label={t('cd.open')} onClick={() => (register ? openDisplay(register.id) : setDialog('display'))} />
            {can('restaurant.configure') ? <IconButton icon="scan" label={t('till.scale_title')} onClick={() => setDialog('scale')} /> : null}
          </div>
        </header>

        <section className="till-pick" aria-label={t('till.pick')}>
          <form
            className={`till-scan${problem ? ' has-problem' : ''}`}
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <Icon name="scan" size={22} />
            <input
              ref={input}
              className="input"
              value={text}
              placeholder={t('till.scan_placeholder')}
              aria-label={t('till.scan_placeholder')}
              autoComplete="off"
              enterKeyHint="go"
              onChange={(e) => {
                setText(e.target.value);
                setProblem(null);
              }}
              aria-describedby="till-problem"
            />
            {text ? <IconButton icon="x" label={t('common.clear')} onClick={() => (setText(''), focus())} /> : null}
          </form>
          <p id="till-problem" className={`till-feedback${problem ? ' text-danger' : fresh ? ' text-ok' : ' muted'}`} role={problem ? 'alert' : 'status'}>
            {problem ? (
              <>
                <Icon name="alert" size={14} /> {problem}
              </>
            ) : fresh ? (
              <>
                <Icon name="check" size={14} /> {t('till.added', { name: fresh.name, qty: fmtQty(fresh.qty) })}
                {fresh.label?.kind === 'weight' ? ` · ${t('till.weighed')}` : fresh.label?.kind === 'price' ? ` · ${t('till.priced', { amount: money(fresh.label.value) })}` : ''}
              </>
            ) : (
              t('till.scan_hint')
            )}
          </p>
          {!text.trim() || isCode(text.trim()) ? (
            <div className="category-tabs" role="tablist">
              {(catalog?.categories ?? []).map((c) => {
                const on = (category ?? catalog?.categories[0]?.id) === c.id;
                return (
                  <button key={c.id} role="tab" aria-selected={on} className={`category-tab tone-${c.tone}${on ? ' active' : ''}`} onClick={() => (setCategory(c.id), focus())}>
                    {c.name}
                  </button>
                );
              })}
            </div>
          ) : null}
          <div className="product-grid till-grid">
            {!catalog
              ? Array.from({ length: 8 }, (_, i) => <Skeleton key={i} h={96} r="var(--radius-md)" />)
              : products.map((p) => (
                  <ProductTile
                    key={p.id}
                    product={p}
                    tone={catalog.categories.find((c) => c.id === p.category)?.tone ?? 'grey'}
                    price={priceNow(catalog, p, new Date(now))}
                    hint={isMaster(p) ? t('product.variants') : null}
                    count={active.filter((l) => l.product === p.id || catalog.products.find((v) => v.id === l.product)?.variant_of === p.id).reduce((n, l) => n + l.qty, 0)}
                    onTap={() => (isMaster(p) ? setDialog({ vary: p.id }) : void addProduct(p.id))}
                  />
                ))}
            {catalog && !products.length ? <p className="muted pad">{t('till.no_match', { text: text.trim() })}</p> : null}
          </div>
        </section>

        <aside className="till-cart" aria-label={t('till.cart')}>
          {order ? <OrderCustomer order={order.id} customer={order.customer ?? null} open onChanged={() => void reload()} /> : null}
          <div className="till-lines">
            {active.length ? (
              active.map((l) => (
                <div key={l.id} className={`till-line${fresh && fresh.product === l.product ? ' just-added' : ''}`}>
                  <span className="till-line-main">
                    <span className="till-line-name">{l.name}</span>
                    <span className="muted small tabular">
                      {fmtQty(l.qty)} × {money(l.unit_price)}
                      {l.price_rule ? ` · ${l.price_rule}` : ''}
                    </span>
                  </span>
                  <span className="till-qty" role="group" aria-label={t('till.qty_of', { name: l.name })}>
                    <IconButton icon={l.qty <= 1 ? 'trash' : 'minus'} label={l.qty <= 1 ? t('till.remove') : t('till.less')} onClick={() => void setQty(l, l.qty <= 1 ? 0 : Math.round((l.qty - 1) * 1000) / 1000)} />
                    <button className="till-qty-n tabular" onClick={() => setDialog({ qty: l })} aria-label={t('till.set_qty', { name: l.name })}>
                      {fmtQty(l.qty)}
                    </button>
                    <IconButton icon="plus" label={t('till.more')} onClick={() => void setQty(l, l.qty + 1)} />
                  </span>
                  <strong className="tabular till-line-total">{money(linePrice(l))}</strong>
                </div>
              ))
            ) : (
              <EmptyState icon="scan" title={t('till.empty')} body={t('till.empty_body')} />
            )}
          </div>
          <div className="till-totals">
            {order && (order.discount > 0 || order.tax_added) ? (
              <>
                <span className="muted">{t('till.subtotal')}</span>
                <span className="tabular">{money(order.subtotal)}</span>
              </>
            ) : null}
            {order && order.discount > 0 ? (
              <>
                <span className="muted">{t('till.discount')}</span>
                <span className="tabular text-ok">−{money(order.discount)}</span>
              </>
            ) : null}
            {order?.tax_added ? (
              <>
                <span className="muted">{t('totals.sales_tax')}</span>
                <span className="tabular">{money(order.tax)}</span>
              </>
            ) : (
              <>
                <span className="muted">{t('till.vat_in', { amount: money(order?.tax ?? 0) })}</span>
                <span />
              </>
            )}
            <span className="till-total-label">{t('till.total')}</span>
            <strong className="till-total tabular">{money(order?.total ?? 0)}</strong>
          </div>
          <div className="till-actions">
            <Button icon="pause" disabled={!active.length} onClick={() => setDialog('hold')}>
              {t('till.hold')}
            </Button>
            {can('sales.discount') ? (
              <Button icon="percent" disabled={!active.length} onClick={() => setDialog('discount')}>
                {t('till.discount')}
              </Button>
            ) : null}
            {can('loyalty.promote') ? <IconButton icon="gift" label={t('promo.till_title')} disabled={!active.length} onClick={() => setDialog('promo')} /> : null}
            <IconButton icon="trash" label={t('till.cancel')} disabled={!active.length} onClick={() => setDialog('cancel')} />
            <Button variant="primary" size="xl" icon="cash" className="till-pay" disabled={!active.length || busy || !can('pos.pay')} onClick={() => setDialog('pay')}>
              {t('till.pay', { amount: money(order?.total ?? 0) })}
            </Button>
          </div>
        </aside>
      </div>

      {dialog === 'display' ? (
        <Dialog title={t('cd.pick_register')} onClose={() => setDialog(null)} size="sm">
          {!registers ? (
            <Skeleton h={80} />
          ) : registers.length ? (
            <div className="menu-list">
              {registers.map((r) => (
                <button
                  key={r.id}
                  className="menu-item"
                  onClick={() => {
                    choose(r.id);
                    setDialog(null);
                    openDisplay(r.id);
                  }}
                >
                  <Icon name="register" />
                  <span>{r.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <EmptyState icon="register" title={t('pay.no_registers')} />
          )}
        </Dialog>
      ) : null}
      {dialog === 'pay' && view ? (
        <PayDialog
          view={view}
          onClose={() => setDialog(null)}
          pay={(p) => action<OrderView & { change: number }>('restaurant.till_pay', { order: view.order.id, ...p })}
          displayKeep={view.order.id}
          next={{ label: t('till.next'), icon: 'scan' }}
          onPaid={() => {
            setDialog(null);
            setView(null);
            setLast(null);
            setHeldVersion((n) => n + 1);
            navigate('/till', { replace: true });
          }}
        />
      ) : null}
      {dialog === 'discount' && view ? <DiscountDialog view={view} onClose={() => setDialog(null)} onDone={show} /> : null}
      {dialog === 'promo' && view ? <PromoDialog view={view} onClose={() => setDialog(null)} onDone={show} /> : null}
      {dialog === 'cancel' && view ? <CancelDialog view={view} onClose={() => setDialog(null)} after="/till" /> : null}
      {dialog === 'hold' && view ? (
        <HoldDialog
          view={view}
          onClose={() => setDialog(null)}
          onHeld={() => {
            setDialog(null);
            setView(null);
            setHeldVersion((n) => n + 1);
            navigate('/till', { replace: true });
            toast(t('till.held_toast'));
          }}
        />
      ) : null}
      {dialog === 'held' ? (
        <HeldDialog
          sales={others}
          current={active.length ? order : null}
          onClose={() => setDialog(null)}
          onPick={(sid) => {
            setDialog(null);
            navigate(`/till/${sid}`);
          }}
        />
      ) : null}
      {dialog && typeof dialog === 'object' && 'vary' in dialog && catalog ? (
        <VariantDialog catalog={catalog} master={dialog.vary} at={new Date(now)} onClose={() => setDialog(null)} onPick={(v) => void addProduct(v)} />
      ) : null}
      {dialog && typeof dialog === 'object' && 'qty' in dialog ? <QtyDialog line={dialog.qty} onClose={() => setDialog(null)} onSet={(q) => (setDialog(null), void setQty(dialog.qty, q))} /> : null}
      {dialog === 'scale' ? <ScaleDialog onClose={() => setDialog(null)} location={location} /> : null}
    </Shell>
  );
}

function HoldDialog({ view, onClose, onHeld }: { view: OrderView; onClose: () => void; onHeld: () => void }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(view.order.name === 'Sale' ? '' : view.order.name);
  const save = () =>
    void run(async () => {
      await action('restaurant.till_hold', { order: view.order.id, name: name.trim() });
      onHeld();
    });
  return (
    <Dialog
      title={t('till.hold_title')}
      onClose={onClose}
      size="sm"
      footer={
        <Button variant="primary" icon="pause" busy={busy} disabled={!name.trim()} onClick={save}>
          {t('till.hold')}
        </Button>
      }
    >
      <form
        className="form-stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) save();
        }}
      >
        <p className="muted">{t('till.hold_intro')}</p>
        <Field label={t('till.hold_name')} hint={t('till.hold_hint')}>
          <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-autofocus />
        </Field>
      </form>
    </Dialog>
  );
}

function HeldDialog({ sales, current, onClose, onPick }: { sales: HeldSale[]; current: OrderView['order'] | null; onClose: () => void; onPick: (id: string) => void }) {
  return (
    <Dialog title={t('till.held_title')} onClose={onClose} size="md">
      <div className="form-stack">
        {current ? (
          <p className="callout">
            <Icon name="info" size={16} /> {t('till.held_current', { n: current.number })}
          </p>
        ) : null}
        <div className="list-card">
          {sales.map((s) => (
            <button key={s.id} className="list-row" onClick={() => onPick(s.id)}>
              <span className="list-icon" aria-hidden="true">
                <Icon name="pause" size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">{s.name === 'Sale' ? t('till.sale_n', { n: s.number }) : s.name}</span>
                <span className="list-sub">{[clock(s.opened_at), tn('till.items', s.items), s.by_name].filter(Boolean).join(' · ')}</span>
              </span>
              <strong className="tabular">{money(s.total)}</strong>
            </button>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

function QtyDialog({ line, onClose, onSet }: { line: Line; onClose: () => void; onSet: (qty: number) => void }) {
  const [text, setText] = useState(String(line.qty));
  const n = Number(text.replace(',', '.'));
  const ok = Number.isFinite(n) && n >= 0 && n <= 9999;
  return (
    <Dialog
      title={line.name}
      onClose={onClose}
      size="sm"
      footer={
        <Button variant="primary" icon="check" disabled={!ok} onClick={() => onSet(Math.round(n * 1000) / 1000)}>
          {n === 0 ? t('till.remove') : t('till.set')}
        </Button>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (ok) onSet(Math.round(n * 1000) / 1000);
        }}
      >
        <Field label={t('till.qty')} hint={t('till.qty_hint')} error={ok ? null : t('till.qty_bad')}>
          <input className="input tabular" inputMode="decimal" value={text} onChange={(e) => setText(e.target.value)} data-autofocus />
        </Field>
      </form>
    </Dialog>
  );
}

const EMPTY_RULE = { name: '', prefix: '', kind: 'weight' as const, item_digits: 5, decimals: 3, active: true };

/** The business's scale labels: how its scales write weight or price into a barcode. */
function ScaleDialog({ onClose, location }: { onClose: () => void; location: string | null }) {
  const [version, setVersion] = useState(0);
  const { data: rules } = useAction<ScaleRule[]>('restaurant.scale_rules', { v: version });
  const [edit, setEdit] = useState<(Omit<ScaleRule, 'id'> & { id?: string }) | null>(null);
  const [code, setCode] = useState('');
  const [tested, setTested] = useState<{ name: string; qty: number; price: number; label: Added['label'] } | string | null>(null);
  const { busy, run } = useSave();
  const left = edit ? 12 - edit.prefix.length - edit.item_digits : 0;

  const save = () =>
    void run(async () => {
      await action('restaurant.save_scale_rule', { ...edit });
      setEdit(null);
      setVersion((v) => v + 1);
    }, t('till.rule_saved'));
  const test = async () => {
    try {
      setTested(await action('restaurant.test_code', { code: code.trim(), location }));
    } catch (err) {
      setTested(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Dialog
      title={t('till.scale_title')}
      onClose={onClose}
      size="lg"
      footer={
        edit ? (
          <>
            <Button variant="ghost" onClick={() => setEdit(null)}>
              {t('common.back')}
            </Button>
            <Button variant="primary" icon="check" busy={busy} disabled={!edit.name.trim() || !/^\d{1,5}$/.test(edit.prefix) || left < 3} onClick={save}>
              {t('till.rule_save')}
            </Button>
          </>
        ) : (
          <Button icon="plus" onClick={() => setEdit({ ...EMPTY_RULE })}>
            {t('till.rule_add')}
          </Button>
        )
      }
    >
      {edit ? (
        <div className="form-stack">
          <div className="form-grid">
            <Field label={t('till.rule_name')}>
              <input className="input" value={edit.name} maxLength={60} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-autofocus />
            </Field>
            <Field label={t('till.rule_prefix')} hint={t('till.rule_prefix_hint')}>
              <input className="input tabular" inputMode="numeric" value={edit.prefix} maxLength={5} onChange={(e) => setEdit({ ...edit, prefix: e.target.value.replace(/\D/g, '') })} />
            </Field>
            <Field label={t('till.rule_kind')}>
              <select className="input" value={edit.kind} onChange={(e) => setEdit({ ...edit, kind: e.target.value as 'weight' | 'price', decimals: e.target.value === 'weight' ? 3 : 2 })}>
                <option value="weight">{t('till.kind_weight')}</option>
                <option value="price">{t('till.kind_price')}</option>
              </select>
            </Field>
            <Field label={t('till.rule_item_digits')} hint={t('till.rule_item_hint')}>
              <input className="input tabular" type="number" min={1} max={6} value={edit.item_digits} onChange={(e) => setEdit({ ...edit, item_digits: Number(e.target.value) })} />
            </Field>
            <Field label={t('till.rule_decimals')} hint={edit.kind === 'weight' ? t('till.rule_dec_weight') : t('till.rule_dec_price')}>
              <input className="input tabular" type="number" min={0} max={3} value={edit.decimals} onChange={(e) => setEdit({ ...edit, decimals: Number(e.target.value) })} />
            </Field>
            <label className="switch-row">
              <span>
                <span className="switch-title">{t('till.rule_active')}</span>
                <span className="muted">{t('till.rule_active_hint')}</span>
              </span>
              <Switch checked={edit.active} onChange={(v) => setEdit({ ...edit, active: v })} label={t('till.rule_active')} />
            </label>
          </div>
          <p className={`scale-layout${left < 3 ? ' text-danger' : ''}`} aria-live="polite">
            <span className="seg seg-prefix">{edit.prefix || 'P'.repeat(Math.max(1, edit.prefix.length))}</span>
            <span className="seg seg-item">{'I'.repeat(Math.max(0, edit.item_digits))}</span>
            <span className="seg seg-value">{'V'.repeat(Math.max(0, left))}</span>
            <span className="seg seg-check">C</span>
            <span className="muted small">{left < 3 ? t('till.rule_too_long') : t('till.rule_layout', { value: left })}</span>
          </p>
        </div>
      ) : (
        <div className="form-stack">
          <p className="muted">{t('till.scale_intro')}</p>
          <div className="list-card">
            {rules === null ? (
              <Skeleton h={60} />
            ) : rules.length ? (
              rules.map((r) => (
                <button key={r.id} className="list-row" onClick={() => setEdit(r)}>
                  <span className="list-icon" aria-hidden="true">
                    <Icon name="scan" size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">{r.name}</span>
                    <span className="list-sub tabular">
                      {t('till.rule_sub', { prefix: r.prefix, digits: r.item_digits, kind: r.kind === 'weight' ? t('till.kind_weight') : t('till.kind_price') })}
                    </span>
                  </span>
                  {r.active ? <StatusBadge tone="ok" icon="check" label={t('till.rule_on')} /> : <StatusBadge tone="neutral" icon="pause" label={t('till.rule_off')} />}
                </button>
              ))
            ) : (
              <p className="muted pad">{t('till.rules_none')}</p>
            )}
          </div>
          <section aria-labelledby="till-test">
            <h3 id="till-test">{t('till.test_title')}</h3>
            <form
              className="till-test"
              onSubmit={(e) => {
                e.preventDefault();
                if (code.trim()) void test();
              }}
            >
              <input className="input tabular" inputMode="numeric" value={code} placeholder={t('till.test_placeholder')} aria-label={t('till.test_placeholder')} onChange={(e) => (setCode(e.target.value), setTested(null))} />
              <Button type="submit" icon="search" disabled={!code.trim()}>
                {t('till.test')}
              </Button>
            </form>
            {tested ? (
              typeof tested === 'string' ? (
                <p className="text-danger small" role="alert">
                  {tested}
                </p>
              ) : (
                <p className="text-ok small" role="status">
                  <Icon name="check" size={14} /> {t('till.test_ok', { name: tested.name, qty: fmtQty(tested.qty), amount: money(Math.round(tested.qty * tested.price)) })}
                </p>
              )
            ) : null}
          </section>
        </div>
      )}
    </Dialog>
  );
}
